import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import OrderDetail from './OrderDetail';
import { isPaymentTermMet } from '../components/OrderTrackingIndicator';
import './OrderDetail.css';
import './SalesOrders.css';

/* ─── helpers ────────────────────────────────────────────────────── */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const STATUS_META = {
  draft:     { label: 'Draft',     color: '#6b7280', bg: '#f3f4f6' },
  confirmed: { label: 'Confirmed', color: '#059669', bg: '#d1fae5' },
  cancelled: { label: 'Cancelled', color: '#ef4444', bg: '#fee2e2' },
  delivered: { label: 'Delivered', color: '#2563eb', bg: '#dbeafe' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="od-badge" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

/* Derive current tracking stage label + CSS tone for a list-row badge */
const TRACKING_TONES = {
  Draft:      { color: '#475569', bg: '#f1f5f9' },
  Cancelled:  { color: '#b91c1c', bg: '#fee2e2' },
  Confirmed:  { color: '#166534', bg: '#dcfce7' },
  Production: { color: '#92400e', bg: '#fef3c7' },
  Ready:      { color: '#5b21b6', bg: '#ede9fe' },
  Delivered:  { color: '#1d4ed8', bg: '#dbeafe' },
};

const TrackingBadge = ({ order, activeTerm }) => {
  if (!order) return null;
  const status = order.status || 'draft';

  let label = 'Draft';
  if (status === 'draft')     label = 'Draft';
  else if (status === 'cancelled') label = 'Cancelled';
  else if (status === 'delivered') label = 'Delivered';
  else {
    const readyEntries = Array.isArray(order.readyEntries) ? order.readyEntries : [];
    const paymentMet   = isPaymentTermMet(order, activeTerm);
    if (readyEntries.length > 0) label = 'Ready';
    else if (paymentMet)         label = 'Production';
    else                         label = 'Confirmed';
  }

  const style = TRACKING_TONES[label] || TRACKING_TONES['Draft'];
  return (
    <span className="od-badge" style={{ color: style.color, background: style.bg }}>
      {label}
    </span>
  );
};

const PAGE_SIZE = 25;

/* ─── List page ─────────────────────────────────────────────────── */

let savedODSearch = '';

const OrderDetailListPage = () => {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState(savedODSearch);
  const [orderFrom, setOrderFrom]       = useState('');
  const [orderTo, setOrderTo]           = useState('');
  const [deliveryFrom, setDeliveryFrom] = useState('');
  const [deliveryTo, setDeliveryTo]     = useState('');
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalCount, setTotalCount]     = useState(0);
  const [activeTerm, setActiveTerm]     = useState(null);
  const navigate = useNavigate();

  // Fetch active payment term once — needed for Production step in indicator
  useEffect(() => {
    API.get('/payment-terms/active')
      .then(({ data }) => setActiveTerm(data.term || null))
      .catch(() => {});
  }, []);

  

  const load = useCallback(async ({
    targetPage  = 1,
    q           = search,
    oFrom       = orderFrom,
    oTo         = orderTo,
    dFrom       = deliveryFrom,
    dTo         = deliveryTo,
  } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      // Only confirmed orders can have Confirmed or Production tracking stage
      params.set('status', 'confirmed');
      if (oFrom)            params.set('orderFrom', oFrom);
      if (oTo)              params.set('orderTo', oTo);
      if (dFrom)            params.set('deliveryFrom', dFrom);
      if (dTo)              params.set('deliveryTo', dTo);
      params.set('page', targetPage);
      params.set('limit', PAGE_SIZE);
      const { data } = await API.get(`/sales-orders?${params}`);
      setOrders(data.orders || []);
      setTotalPages(data.pages || 1);
      setTotalCount(data.count || 0);
      setPage(targetPage);
    } catch {
      toast.error('Could not load orders');
    } finally {
      setLoading(false);
    }
  }, [search, orderFrom, orderTo, deliveryFrom, deliveryTo]);

  // Initial load
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync search to module variable so back navigation restores it
  useEffect(() => {
    savedODSearch = search;
  }, [search]);

  // Search — debounced, reset to page 1
  useEffect(() => {
    const t = setTimeout(() => load({ targetPage: 1, q: search }), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date filters — immediate, reset to page 1
  useEffect(() => {
    load({ targetPage: 1, oFrom: orderFrom, oTo: orderTo, dFrom: deliveryFrom, dTo: deliveryTo });
  }, [orderFrom, orderTo, deliveryFrom, deliveryTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive tracking label for each order, then keep only Confirmed & Production
  const getTrackingLabel = (order) => {
    const status = order.status || 'draft';
    if (status === 'draft')      return 'Draft';
    if (status === 'cancelled')  return 'Cancelled';
    if (status === 'delivered')  return 'Delivered';
    const readyEntries = Array.isArray(order.readyEntries) ? order.readyEntries : [];
    const paymentMet   = isPaymentTermMet(order, activeTerm);
    if (readyEntries.length > 0) return 'Ready';
    if (paymentMet)              return 'Production';
    return 'Confirmed';
  };

  const sorted = [...orders].filter((o) => {
    const label = getTrackingLabel(o);
    return label === 'Confirmed' || label === 'Production';
  });

  const hasDateFilter = orderFrom || orderTo || deliveryFrom || deliveryTo;
  const hasFilter     = hasDateFilter;

  return (
    <div className="so-page">
      <div className="so-page-head">
        <div>
          <h2>Designing</h2>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="so-filter-bar">
        <div className="so-search-input so-search-input--filter">
          <FiSearch size={15} />
          <input
            placeholder="Search order no., customer, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Order date range */}
        <div className="so-filter-dates">
          <span className="so-date-label">Order</span>
          <input
            type="date"
            className="so-date-input"
            title="Order from"
            value={orderFrom}
            onChange={(e) => setOrderFrom(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
          <span className="so-date-sep">–</span>
          <input
            type="date"
            className="so-date-input"
            title="Order to"
            value={orderTo}
            onChange={(e) => setOrderTo(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>

        {/* Delivery date range */}
        <div className="so-filter-dates">
          <span className="so-date-label">Delivery</span>
          <input
            type="date"
            className="so-date-input"
            title="Delivery from"
            value={deliveryFrom}
            onChange={(e) => setDeliveryFrom(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
          <span className="so-date-sep">–</span>
          <input
            type="date"
            className="so-date-input"
            title="Delivery to"
            value={deliveryTo}
            onChange={(e) => setDeliveryTo(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>

        {hasFilter && (
          <button className="so-clear-btn" onClick={() => {
            setOrderFrom(''); setOrderTo('');
            setDeliveryFrom(''); setDeliveryTo('');
          }}>Clear</button>
        )}
      </div>

      {/* ── Table card ── */}
      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="so-empty">
            {search || hasFilter ? 'No matching orders.' : 'No orders yet.'}
          </div>
        ) : (
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Order Date</th>
                  <th>Delivery Date</th>
                  <th>Tracking</th>
                  <th style={{ textAlign: 'right' }}>Grand Total</th>
                  <th style={{ textAlign: 'right' }}>Balance Due</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((o) => {
                  const balance = (Number(o.grandTotal) || 0) - (Number(o.amountReceived) || 0);
                  return (
                    <tr
                      key={o.id}
                      onClick={() => navigate(`/admin/order-detail/${o.id}`)}
                    >
                      <td className="so-strong" style={{ color: 'var(--primary)' }}>
                        {o.orderNumber}
                      </td>
                      <td>
                        <div className="so-strong">{o.companyName || o.customerName}</div>
                        {o.companyName && (
                          <div className="so-muted">{o.customerName}</div>
                        )}
                        {!o.companyName && o.customerPhone && (
                          <div className="so-muted">{o.customerPhone}</div>
                        )}
                      </td>
                      <td className="so-muted">{fmtDate(o.orderDate)}</td>
                      <td className="so-muted">{fmtDate(o.deliveryDate)}</td>
                      <td>
                        <TrackingBadge order={o} activeTerm={activeTerm} />
                      </td>
                      <td style={{ textAlign: 'right' }} className="so-strong">
                        {money(o.grandTotal)}
                      </td>
                      <td style={{
                        textAlign: 'right',
                        fontWeight: 600,
                        color: balance > 0 ? '#ef4444' : '#059669',
                      }}>
                        {money(balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Page {page} of {totalPages} &nbsp;·&nbsp; {totalCount} orders
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page - 1 })}
              disabled={page <= 1}
              style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: page <= 1 ? 0.4 : 1 }}
            >
              ‹ Prev
            </button>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page + 1 })}
              disabled={page >= totalPages}
              style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: page >= totalPages ? 0.4 : 1 }}
            >
              Next ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Router wrapper ─────────────────────────────────────────────── */

const OrderDetailModule = () => (
  <Routes>
    <Route index element={<OrderDetailListPage />} />
    <Route path=":id" element={<OrderDetail />} />
  </Routes>
);

export default OrderDetailModule;